// hooks/useTwilioCall.js
import { useState, useEffect, useCallback } from "react";
import twilioService from "../utils/twilioService";

export const useTwilioCall = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [callStatus, setCallStatus] = useState("idle"); // 'idle', 'initializing', 'ready', 'calling', 'connected', 'disconnected', 'error'
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState(null);
  const [currentProspect, setCurrentProspect] = useState(null);

  // Initialize Twilio with token
  const initialize = useCallback(async token => {
    try {
      setCallStatus("initializing");
      setError(null);

      // Set up callbacks
      twilioService.on("onReady", () => {
        setIsInitialized(true);
        setCallStatus("ready");
      });

      twilioService.on("onError", error => {
        console.error("Twilio error:", error);
        setError(error.message);
        setCallStatus("error");
        setIsCallActive(false);
      });

      twilioService.on("onCallStart", () => {
        setIsCallActive(true);
        setCallStatus("connected");
      });

      twilioService.on("onCallEnd", () => {
        setIsCallActive(false);
        setCallStatus("disconnected");
        setCallDuration(0);
      });

      twilioService.on("onCallStatusChange", ({ status, duration, error }) => {
        setCallStatus(status);
        if (duration !== undefined) {
          setCallDuration(duration);
        }
        if (error) {
          setError(error);
        }
      });

      // Initialize Twilio
      await twilioService.initialize(token);
    } catch (err) {
      console.error("Failed to initialize Twilio:", err);
      setError(err.message);
      setCallStatus("error");
      throw err;
    }
  }, []);

  // Make a call
  const makeCall = useCallback(
    async (phoneNumber, prospect) => {
      if (!isInitialized) {
        throw new Error("Twilio not initialized");
      }

      if (!phoneNumber) {
        throw new Error("Phone number is required");
      }

      try {
        setCurrentProspect(prospect);
        setError(null);
        const call = await twilioService.makeCall(phoneNumber, prospect);
        return call;
      } catch (err) {
        console.error("Call failed:", err);
        setError(err.message);
        throw err;
      }
    },
    [isInitialized]
  );

  // Hang up
  const hangUp = useCallback(async () => {
    try {
      await twilioService.hangUp();
      setIsCallActive(false);
      setCurrentProspect(null);
    } catch (err) {
      console.error("Error hanging up:", err);
      setError(err.message);
    }
  }, []);

  // Format phone number
  const formatPhoneNumber = useCallback(phone => {
    return twilioService.formatPhoneNumber(phone);
  }, []);

  // Get call duration formatted
  const getFormattedDuration = useCallback(() => {
    const mins = Math.floor(callDuration / 60);
    const secs = callDuration % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }, [callDuration]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (isCallActive) {
        hangUp();
      }
    };
  }, [isCallActive, hangUp]);

  return {
    // State
    isInitialized,
    isCallActive,
    callStatus,
    callDuration,
    error,
    currentProspect,

    // Actions
    initialize,
    makeCall,
    hangUp,
    formatPhoneNumber,
    getFormattedDuration,

    // Helpers
    setError,
  };
};
